/**
 * FolderIconSvg · Win11 风格文件夹图标（v3）
 *
 * variant（仅决定底色）
 *  · 'manual' 黄色调 · 普通文件夹 / 智能分类文件夹共用（见下方 isAi 说明）
 *  · 'gray'   银灰调 · 待归档虚拟项，与彩色文件夹区分"非真实分类"
 *
 * isAi（智能分类文件夹标记）
 *  · true → 在图标右下角叠加一个刷新（autorenew）水印
 *  · 智能分类文件夹不再用蓝色区分，而是「黄色底 + 刷新水印」对齐普通文件夹，
 *    仅靠右下角水印表明它是 AI 自动归类的相册（v3 改动，原 v2 为蓝色 'auto'）
 *
 * 形态分支
 *  · hasChildren=true   · 在背板和前板之间夹一张白纸 · 露顶角营造"含内容"
 *  · hasChildren=false  · 标准空 folder
 *
 * 视觉设计：
 *  · 全部去掉 stroke 描边 · 仅靠纯色填充 + 上下渐变 · 与 Win11 默认 folder 一致
 *  · 去掉前板顶沿的高光线 · 避免低分屏被白纸顶部 #FAFAFA 串扰
 *  · 背板顶部 tab 轻微圆角，前板覆盖时露 6px tab，符合 Win11 比例
 */

import React, { useRef } from 'react'

interface FolderIconSvgProps {
  /** 整图宽度（px），高度按 0.85 比例自动计算 */
  size?: number
  /** 配色变体 · manual 黄(普通/智能分类共用) / gray 银灰(待归档虚拟项) */
  variant: 'manual' | 'gray'
  /** 是否智能分类文件夹 · true 时在右下角叠加刷新水印 */
  isAi?: boolean
  /** 有子文件夹 · 切到"含内容"样式（露纸角）*/
  hasChildren?: boolean
}

interface Palette {
  /** 背板上沿（更亮） */
  backTop: string
  /** 背板下沿（更暗） */
  backBottom: string
  /** 前板上沿 */
  frontTop: string
  /** 前板下沿 */
  frontBottom: string
}

const PALETTE: Record<'manual' | 'gray', Palette> = {
  // 黄色 · 取自 Win11 默认 folder 的金黄渐变 · 普通 + 智能分类共用
  manual: {
    backTop: '#FFD37A',
    backBottom: '#E0A93C',
    frontTop: '#FFE19A',
    frontBottom: '#F2B748'
  },
  // 银灰 · 待归档虚拟项 · 弱化处理，与彩色文件夹区分"非真实分类"
  gray: {
    backTop: '#D6DAE0',
    backBottom: '#AEB4BE',
    frontTop: '#E3E6EB',
    frontBottom: '#BCC2CC'
  }
}

// 模块级计数器 · 给每个 SVG 实例分配唯一 gradient id，避免多实例
// 共用同一 ID 时浏览器只取首个 def 导致颜色串
let __folderIconUid = 0

const FolderIconSvg: React.FC<FolderIconSvgProps> = ({
  size = 64,
  variant,
  isAi = false,
  hasChildren = false
}) => {
  const p = PALETTE[variant]
  const uidRef = useRef<number | null>(null)
  if (uidRef.current === null) uidRef.current = ++__folderIconUid
  const uid = uidRef.current
  const backGradId = `fld-back-${uid}`
  const frontGradId = `fld-front-${uid}`

  return (
    <svg
      width={size}
      height={size * 0.85}
      viewBox="0 0 64 54"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={backGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.backTop} />
          <stop offset="100%" stopColor={p.backBottom} />
        </linearGradient>
        <linearGradient id={frontGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.frontTop} />
          <stop offset="100%" stopColor={p.frontBottom} />
        </linearGradient>
      </defs>

      {/* 背板 · 含上方梯形 tab · 圆角 4 · 无描边 */}
      <path
        d="M4 12
           Q4 8 8 8
           L22 8
           Q24 8 25.6 9.6
           L28 12
           L56 12
           Q60 12 60 16
           L60 48
           Q60 52 56 52
           L8 52
           Q4 52 4 48
           Z"
        fill={`url(#${backGradId})`}
      />

      {/* 内含纸张 · 仅 hasChildren · 露顶角营造"folder 里有内容" */}
      {hasChildren && (
        <>
          {/* 后纸 · 露 ~3px */}
          <rect
            x="11"
            y="15"
            width="42"
            height="32"
            rx="2"
            fill="#FFFFFF"
            opacity="0.95"
          />
          {/* 前纸 · 错开露双层效果 */}
          <rect x="8.5" y="17" width="47" height="32" rx="2" fill="#F5F5F5" />
        </>
      )}

      {/* 前板 · 顶在 y=18，比背板矮 6px tab 露出 · 无描边 */}
      <path
        d="M4 22
           Q4 18 8 18
           L56 18
           Q60 18 60 22
           L60 48
           Q60 52 56 52
           L8 52
           Q4 52 4 48
           Z"
        fill={`url(#${frontGradId})`}
      />

      {/* 智能分类水印 · 右下角刷新（autorenew）图标 · 白色半透明
          · 路径取自 Material Icons autorenew（viewBox 24），transform 缩放定位到前板右下角 */}
      {isAi && (
        <g
          transform="translate(36 30) scale(0.85)"
          fill="#FFFFFF"
          fillOpacity={0.92}
        >
          <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z" />
        </g>
      )}
    </svg>
  )
}

export default FolderIconSvg
